import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { DemoPill } from '@/components/ui/controls';
import type { ContactKind, LeadDetail } from '@/types/api';
import { countryName, formatDate, formatDateTime, formatRelative } from '@/utils/format';
import { casinoStatusLabel, channelLabel, sourceLabel } from '@/utils/labels';

/* Pieces of a lead's record shared by the side panel and the full lead page. */

export const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="grid grid-cols-[120px_1fr] gap-3 py-1 text-sm">
    <dt className="text-fg-muted">{label}</dt>
    <dd className="min-w-0 break-words text-fg">{children}</dd>
  </div>
);

export const Empty = ({ children }: { children: ReactNode }) => <p className="text-sm text-fg-faint">{children}</p>;

/** Markers of the demo seed and the mock summarizer (backend/deploy/local/prisma_demo.sql). */
export const isDemo = (s: string) => s === 'demo' || s === 'demo-contact' || s === 'mock';

/** The latest AI conversation summary (TG-4, TG-5). */
export const SummaryBody = ({ lead }: { lead: LeadDetail }) =>
  lead.latest_summary ? (
    <>
      <p className="text-sm leading-relaxed text-fg">{lead.latest_summary.summary}</p>
      <p className="mt-2 text-xs text-fg-faint">Covers messages until {formatDateTime(lead.latest_summary.covered_until)}</p>
    </>
  ) : (
    <Empty>No summary yet. It appears once the lead's Telegram group has messages.</Empty>
  );

export const SummaryDemo = ({ lead }: { lead: LeadDetail }) =>
  lead.latest_summary && isDemo(lead.latest_summary.model) ? <DemoPill title="Written by the demo seed, not by the AI">Demo</DemoPill> : null;

const contactLink = 'inline-flex min-h-11 items-center break-all text-brand hover:underline md:min-h-0';

// How each kind of contact opens: a call, a WhatsApp chat, a profile, a site.
const CONTACTS: { kind: ContactKind; label: string; href?: (v: string) => string; text?: (v: string) => string }[] = [
  { kind: 'phone', label: 'Phone', href: (v) => `tel:${v}` },
  { kind: 'whatsapp', label: 'WhatsApp', href: (v) => `https://wa.me/${v.replace(/^\+/, '')}` },
  { kind: 'instagram', label: 'Instagram', href: (v) => `https://instagram.com/${v}`, text: (v) => `@${v}` },
  { kind: 'linkedin', label: 'LinkedIn', href: (v) => v, text: (v) => v.replace(/^https?:\/\/(www\.)?/, '') },
  { kind: 'x', label: 'X', href: (v) => `https://x.com/${v}`, text: (v) => `@${v}` },
  { kind: 'discord', label: 'Discord' },
  { kind: 'website', label: 'Website', href: (v) => v, text: (v) => v.replace(/^https?:\/\//, '') },
];

/** Who the lead is (LEAD-1…7), and the ways to reach them: tapping one calls, chats or opens the profile. */
export const DetailsList = ({ lead }: { lead: LeadDetail }) => (
  <dl>
    <Field label="Company">{lead.company || '—'}</Field>
    <Field label="Country">{lead.country ? countryName(lead.country) : '—'}</Field>
    {lead.event && <Field label="Met at">{lead.event}</Field>}
    {CONTACTS.filter((c) => lead.contacts?.[c.kind]).map((c) => {
      const v = lead.contacts[c.kind] as string;
      const text = c.text ? c.text(v) : v;
      return (
        <Field key={c.kind} label={c.label}>
          {c.href ? (
            <a href={c.href(v)} className={contactLink} target={c.kind === 'phone' ? undefined : '_blank'} rel="noopener noreferrer">
              {text}
            </a>
          ) : (
            text
          )}
        </Field>
      );
    })}
    <Field label="Owners Hub">{lead.owner_user_id ? 'Has an account' : 'No account yet'}</Field>
    <Field label="Added">{formatDate(lead.created_at)}</Field>
    <Field label="Last seen">{formatRelative(lead.last_seen_at)}</Field>
    {lead.notes && (
      <Field label="Notes">
        <span className="whitespace-pre-wrap">{lead.notes}</span>
      </Field>
    )}
  </dl>
);

/** Where the lead came from: the first touch wins (ATT-1…5). */
export const SourceBody = ({ lead }: { lead: LeadDetail }) => {
  const first = lead.touches[0];
  if (!first) return <Empty>Unknown. The team can ask in Telegram (ATT-2).</Empty>;
  return (
    <dl>
      <Field label="First touch">
        {sourceLabel(first.source)}
        {first.event && <span className="text-fg-muted"> · {first.event}</span>}
        {first.channel !== 'manual' && <span className="text-fg-muted"> via {channelLabel(first.channel).toLowerCase()}</span>}
      </Field>
      {first.utm_campaign && <Field label="Campaign">{first.utm_campaign}</Field>}
      {first.referrer && <Field label="Referrer">{first.referrer}</Field>}
      {first.landing_url && <Field label="Landing page">{first.landing_url}</Field>}
      {first.channel === 'manual' && <Field label="Added by">{first.added_by || 'A team member'}</Field>}
      <Field label="When">{formatDate(first.occurred_at)}</Field>
      {lead.touches.length > 1 && <Field label="Later touches">{lead.touches.length - 1}</Field>}
    </dl>
  );
};

/** The contact form's questions and answers (LEAD-2). */
export const FormAnswers = ({ lead }: { lead: LeadDetail }) => {
  const form = lead.contact_submissions[0];
  if (!form) return <Empty>This lead has not used the contact form.</Empty>;
  return (
    <dl>
      {form.answers.map((a) => (
        <Field key={a.question} label={a.question}>
          {a.answer}
        </Field>
      ))}
      <p className="mt-1 text-xs text-fg-faint">Sent {formatDateTime(form.submitted_at)}</p>
    </dl>
  );
};

export const FormDemo = ({ lead }: { lead: LeadDetail }) => {
  const form = lead.contact_submissions[0];
  return form && isDemo(form.form) ? <DemoPill title="From the demo seed">Demo</DemoPill> : null;
};

/** The casinos this lead started (CAS-1, CAS-3). `linked` names link to each casino's page. */
export const CasinoList = ({ lead, linked = false }: { lead: LeadDetail; linked?: boolean }) => (
  <>
    {lead.casinos.length ? (
      <ul className="space-y-2">
        {lead.casinos.map((c) => (
          <li key={c.id} className="flex items-center justify-between gap-3 rounded-lg bg-surface-inset px-3 py-2 text-sm">
            <div className="min-w-0">
              {linked ? (
                <Link to={`/casinos/${encodeURIComponent(c.id)}`} className="block truncate py-3 text-fg-strong hover:text-brand md:py-0">
                  {c.brand_name || 'Unnamed casino'}
                </Link>
              ) : (
                <p className="truncate text-fg-strong">{c.brand_name || 'Unnamed casino'}</p>
              )}
              <p className="truncate text-xs text-fg-muted">{[c.domain, c.country && countryName(c.country)].filter(Boolean).join(' · ') || '—'}</p>
            </div>
            <span className="shrink-0 text-xs text-fg-muted">{casinoStatusLabel(c.status)}</span>
          </li>
        ))}
      </ul>
    ) : (
      <Empty>No casino yet.</Empty>
    )}
    {lead.casinos.length > 0 && (
      <Link
        to={`/casinos?q=${encodeURIComponent(lead.email)}`}
        className="mt-1 inline-flex min-h-11 items-center text-sm text-brand hover:underline md:mt-2 md:inline-block md:min-h-0"
      >
        Show on the Casinos page
      </Link>
    )}
  </>
);
